// src/Dashboard/pages/user/Lexique/LexiqueDashboard.jsx

import React from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";

import { useLexique } from "./hooks/useLexique";

import LexiqueHeader from "./components/LexiqueHeader";
import LexiqueKpis from "./components/LexiqueKpis";
import LexiqueToolbar from "./components/LexiqueToolbar";
import LexiqueRow from "./components/LexiqueRow";
import LexiqueSkeleton from "./components/LexiqueSkeleton";
import LexiqueEmptyState from "./components/LexiqueEmptyState";

export default function LexiqueDashboard() {
  useScrollToTop();

  const {
    discipline,
    loading,
    error,

    data,
    filtered,
    visibleRows,
    groups,
    availableLetters,
    stats,

    ui,
    debouncedQ,
    setDebouncedQ,
    updateUi,
    resetFilters,
    loadMore,

    favorites,
    isFavorite,
    toggleFavorite,

    shareCopied,
    copyShare,
    exportCSV,
    refresh,
  } = useLexique();

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] space-y-5 px-3 sm:px-4 lg:px-6">
        <LexiqueHeader
          discipline={discipline}
          loading={loading}
          onRefresh={refresh}
          onExport={exportCSV}
        />

        <LexiqueKpis stats={stats} loading={loading} />

        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="p-4 sm:p-5 lg:p-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Vocabulaire technique
                </p>

                <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                  Lexique {discipline || "Judo"}
                </h2>

                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Retrouvez les termes japonais et leur traduction française.
                  Vous pouvez rechercher, filtrer par lettre, écouter la
                  prononciation, mettre des favoris et exporter la liste
                  filtrée.
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800/60">
                <span className="text-slate-500 dark:text-slate-400">
                  Affichés :
                </span>{" "}
                <span className="font-bold text-slate-900 dark:text-white">
                  {visibleRows.length}
                </span>
                <span className="text-slate-500 dark:text-slate-400">
                  {" "}
                  / {filtered.length}
                </span>
                <span className="text-slate-500 dark:text-slate-400">
                  {" "}
                  résultat{filtered.length > 1 ? "s" : ""}
                </span>
              </div>
            </div>

            <div className="mt-5">
              <LexiqueToolbar
                ui={ui}
                availableLetters={availableLetters}
                debouncedQ={debouncedQ}
                setDebouncedQ={setDebouncedQ}
                updateUi={updateUi}
                onReset={resetFilters}
                onShare={copyShare}
                shareCopied={shareCopied}
                onExport={exportCSV}
                favoritesCount={favorites.length}
              />
            </div>

            {loading ? (
              <div className="mt-5 overflow-hidden rounded-[24px] border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950">
                {Array.from({ length: 14 }).map((_, index) => (
                  <LexiqueSkeleton key={index} />
                ))}
              </div>
            ) : error ? (
              <div className="mt-5 rounded-[24px] border border-red-200 bg-red-50 px-5 py-8 text-center dark:border-red-500/20 dark:bg-red-500/10">
                <p className="text-sm font-bold text-red-700 dark:text-red-300">
                  {error}
                </p>

                <button
                  type="button"
                  onClick={refresh}
                  className="mt-4 rounded-2xl bg-red-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-red-700"
                >
                  Réessayer
                </button>
              </div>
            ) : filtered.length === 0 ? (
              <LexiqueEmptyState onReset={resetFilters} />
            ) : (
              <>
                <div className="mt-5 overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
                  {groups.map(({ letter, items }) => (
                    <section key={letter}>
                      <header className="border-b border-slate-200 bg-slate-50 px-4 py-2 dark:border-slate-800 dark:bg-slate-900">
                        <div className="flex items-center justify-between gap-3">
                          <h3 className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                            Lettre {letter}
                          </h3>

                          <span className="rounded-full bg-white px-2.5 py-1 text-[10px] font-bold text-slate-500 ring-1 ring-slate-200 dark:bg-slate-950 dark:text-slate-400 dark:ring-slate-800">
                            {items.length} terme
                            {items.length > 1 ? "s" : ""}
                          </span>
                        </div>
                      </header>

                      <ul className="divide-y divide-slate-200/80 dark:divide-slate-800/80">
                        {items.map((row) => (
                          <LexiqueRow
                            key={`${row.japonais}-${row.traduction}`}
                            row={row}
                            q={ui.q}
                            isFav={isFavorite(row)}
                            toggleFav={() => toggleFavorite(row)}
                          />
                        ))}
                      </ul>
                    </section>
                  ))}
                </div>

                {visibleRows.length < filtered.length && (
                  <div className="mt-6 flex justify-center">
                    <button
                      type="button"
                      onClick={loadMore}
                      className="rounded-2xl bg-[#013E8E] px-5 py-3 text-sm font-bold text-white transition hover:bg-indigo-800"
                    >
                      Charger plus
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>

      <ToastContainer />
    </section>
  );
}
